import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { Link } from 'react-router-dom'
import {
  CheckCircle,
  AlertTriangle,
  TrendingUp,
  BarChart3,
  Activity,
  Eye,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  ExternalLink,
} from 'lucide-react'
import { KPICard, KPICardSkeleton, type KPICardData } from '@/components/shared/KPICard'

interface BrokerHealth {
  broker_id: number
  name: string
  total: number
  healthy: number
  errors: number
  pending: number
  last_run: string | null
}

interface HealthData {
  kpis: {
    active_projects: { healthy: number; total: number }
    issues_count: number
    success_rate: number
    weekly_activity: {
      sales_detected: number
      price_changes: number
      new_units: number
      units_updated: number
      ghost_units_cleaned: number
      ghost_units_pending: number
    }
  }
  broker_health: BrokerHealth[]
}

interface HealthDashboardProps {
  data: HealthData | undefined
  isLoading: boolean
  onKpiClick?: (kpiId: string) => void
}

type BrokerStatusFilter = 'all' | 'healthy' | 'errors' | 'pending'

function BarLegend() {
  return (
    <div className="flex items-center gap-4 text-xs text-secondary-500">
      <div className="flex items-center gap-1.5">
        <div className="w-3 h-3 rounded-sm bg-green-500" />
        <span>Healthy</span>
      </div>
      <div className="flex items-center gap-1.5">
        <div className="w-3 h-3 rounded-sm bg-red-500" />
        <span>Errors / Stale</span>
      </div>
      <div className="flex items-center gap-1.5">
        <div className="w-3 h-3 rounded-sm bg-amber-400" />
        <span>Pending</span>
      </div>
      <div className="flex items-center gap-1.5">
        <div className="w-3 h-3 rounded-sm bg-secondary-200" />
        <span>No data</span>
      </div>
    </div>
  )
}

function getProjectScrapStatusCategory(project: any): 'healthy' | 'errors' | 'pending' {
  const status = project.scrap_status
  const lastScraped = project.last_scraped_at ? new Date(project.last_scraped_at) : null
  const tenDaysAgo = new Date(Date.now() - 10 * 24 * 60 * 60 * 1000)

  if (status === 'http_error' || status === 'other_error') return 'errors'
  if (status === 'successful' && lastScraped && lastScraped < tenDaysAgo) return 'errors'
  if (status === 'successful' && lastScraped && lastScraped >= tenDaysAgo) return 'healthy'
  return 'pending'
}

function scrapStatusBadge(project: any) {
  const category = getProjectScrapStatusCategory(project)
  if (category === 'healthy') {
    return <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">Healthy</span>
  }
  if (category === 'errors') {
    const isStale = project.scrap_status === 'successful'
    return (
      <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700">
        {isStale ? 'Stale' : project.scrap_status?.replaceAll('_', ' ') || 'Error'}
      </span>
    )
  }
  return <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">{project.scrap_status || 'No data'}</span>
}

function BrokerProjectsTable({ broker }: { broker: BrokerHealth }) {
  const [statusFilter, setStatusFilter] = useState<BrokerStatusFilter>('all')

  const { data: projectsData, isLoading } = useQuery({
    queryKey: ['broker-projects', broker.broker_id],
    queryFn: async () => {
      const brokerId = broker.broker_id === 0 ? 'null' : broker.broker_id
      const response = await api.get(`/projects?broker_id=${brokerId}&sold_out=false&limit=500`)
      return response.data
    },
  })

  const projects: any[] = Array.isArray(projectsData?.data) ? projectsData.data : Array.isArray(projectsData) ? projectsData : []

  // Compute counts from actual fetched data so pill numbers match filtered rows exactly
  const categoryCounts = projects.reduce(
    (acc: Record<string, number>, p: any) => {
      const cat = getProjectScrapStatusCategory(p)
      acc[cat] = (acc[cat] || 0) + 1
      return acc
    },
    {} as Record<string, number>,
  )

  const filteredProjects = projects.filter((p: any) => {
    if (statusFilter === 'all') return true
    return getProjectScrapStatusCategory(p) === statusFilter
  })

  const filterTabs: { key: BrokerStatusFilter; label: string; count: number; color: string }[] = [
    { key: 'all', label: 'All', count: projects.length, color: 'text-secondary-700' },
    { key: 'healthy', label: 'Healthy', count: categoryCounts.healthy || 0, color: 'text-green-700' },
    { key: 'errors', label: 'Errors / Stale', count: categoryCounts.errors || 0, color: 'text-red-700' },
    { key: 'pending', label: 'Pending', count: categoryCounts.pending || 0, color: 'text-amber-700' },
  ]

  if (isLoading) {
    return (
      <div className="px-4 py-6">
        <div className="flex gap-2 mb-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-7 w-20 animate-pulse bg-secondary-200 rounded-full" />
          ))}
        </div>
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="flex items-center space-x-4 py-2">
            <div className="h-4 w-48 animate-pulse bg-secondary-200 rounded" />
            <div className="h-4 w-24 animate-pulse bg-secondary-200 rounded" />
            <div className="h-4 w-16 animate-pulse bg-secondary-200 rounded" />
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="px-4 pb-4">
      {/* Filter tabs */}
      <div className="flex items-center gap-2 mb-3">
        {filterTabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setStatusFilter(tab.key)}
            className={`text-xs font-medium px-3 py-1.5 rounded-full transition-colors ${
              statusFilter === tab.key
                ? 'bg-primary-100 text-primary-700 ring-1 ring-primary-300'
                : 'bg-secondary-100 text-secondary-600 hover:bg-secondary-200'
            }`}
          >
            {tab.label}
            <span className="ml-1 opacity-70">({tab.count})</span>
          </button>
        ))}
      </div>

      {/* Projects table */}
      {filteredProjects.length === 0 ? (
        <p className="text-sm text-secondary-400 py-4 text-center">No projects match this filter.</p>
      ) : (
        <div className="overflow-x-auto max-h-80 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10 bg-secondary-50">
              <tr className="text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                <th className="py-2 pr-4">Project</th>
                <th className="py-2 pr-4">Status</th>
                <th className="py-2 pr-4">Last Scraped</th>
                <th className="py-2 pr-4">Error</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-secondary-100">
              {filteredProjects.map((project: any) => {
                const projectId = project.project_id || project.id
                const lastScraped = project.last_scraped_at
                  ? new Date(project.last_scraped_at).toLocaleDateString('pt-PT')
                  : 'Never'

                return (
                  <tr key={projectId} className="hover:bg-secondary-50 transition-colors">
                    <td className="py-2 pr-4">
                      <Link
                        to={`/projects/${projectId}`}
                        className="text-primary-600 hover:text-primary-800 font-medium hover:underline"
                      >
                        {project.name || 'Unknown'}
                      </Link>
                    </td>
                    <td className="py-2 pr-4">{scrapStatusBadge(project)}</td>
                    <td className="py-2 pr-4 text-secondary-600">{lastScraped}</td>
                    <td className="py-2 pr-4 text-secondary-500 max-w-xs truncate" title={project.scrap_error_message || ''}>
                      {project.scrap_error_message || '-'}
                    </td>
                    <td className="py-2">
                      {project.website && (
                        <a
                          href={project.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-secondary-400 hover:text-primary-600"
                          title="Open project website"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function BrokerHealthBar({
  broker,
  isSelected,
  onSelect,
}: {
  broker: BrokerHealth
  isSelected: boolean
  onSelect: () => void
}) {
  const healthPercent = broker.total > 0 ? (broker.healthy / broker.total) * 100 : 0
  const errorPercent = broker.total > 0 ? (broker.errors / broker.total) * 100 : 0
  const pendingPercent = broker.total > 0 ? ((broker.pending || 0) / broker.total) * 100 : 0

  return (
    <div>
      <div
        className={`flex items-center space-x-4 py-3 cursor-pointer rounded-lg transition-colors ${
          isSelected ? 'bg-primary-50' : 'hover:bg-secondary-50'
        }`}
        onClick={onSelect}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onSelect() }}
      >
        <div className="w-40 flex-shrink-0 pl-2">
          <div className="flex items-center gap-1">
            <ChevronRight className={`h-3.5 w-3.5 text-secondary-400 transition-transform ${isSelected ? 'rotate-90' : ''}`} />
            <p className="text-sm font-medium text-secondary-900 truncate">{broker.name}</p>
          </div>
          <p className="text-xs text-secondary-400 ml-5">
            Last run: {broker.last_run ? new Date(broker.last_run).toLocaleDateString('pt-PT') : 'Never'}
          </p>
        </div>
        <div className="flex-1">
          <div className="w-full bg-secondary-200 rounded-full h-2.5 overflow-hidden">
            <div className="h-full flex">
              <div
                className="bg-green-500 h-full transition-all duration-300"
                style={{ width: `${healthPercent}%` }}
              />
              <div
                className="bg-red-500 h-full transition-all duration-300"
                style={{ width: `${errorPercent}%` }}
              />
              {pendingPercent > 0 && (
                <div
                  className="bg-amber-400 h-full transition-all duration-300"
                  style={{ width: `${pendingPercent}%` }}
                />
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-3 flex-shrink-0">
          <span className="text-sm text-secondary-600">
            {broker.healthy}/{broker.total} OK
          </span>
          {broker.errors > 0 && (
            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700">
              {broker.errors} errors
            </span>
          )}
        </div>
      </div>
      {isSelected && <BrokerProjectsTable broker={broker} />}
    </div>
  )
}

const BROKER_DISPLAY_LIMIT = 5

export function HealthDashboard({ data, isLoading, onKpiClick }: HealthDashboardProps) {
  const [showAllBrokers, setShowAllBrokers] = useState(false)
  const [selectedBrokerId, setSelectedBrokerId] = useState<number | null>(null)

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <KPICardSkeleton key={i} />
          ))}
        </div>
        <div className="card">
          <div className="h-6 w-32 animate-pulse bg-secondary-200 rounded mb-4" />
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex items-center space-x-4 py-3">
              <div className="h-4 w-32 animate-pulse bg-secondary-200 rounded" />
              <div className="flex-1 h-2.5 animate-pulse bg-secondary-200 rounded-full" />
              <div className="h-4 w-20 animate-pulse bg-secondary-200 rounded" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  const kpis = data?.kpis
  const weekly = kpis?.weekly_activity

  const successRate = kpis?.success_rate ?? 0

  const cards: KPICardData[] = [
    {
      id: 'active-projects',
      label: 'Active Projects',
      value: kpis?.active_projects?.total ?? 0,
      detail: `${kpis?.active_projects?.healthy ?? 0} healthy`,
      icon: CheckCircle,
      color: 'green',
    },
    {
      id: 'issues',
      label: 'Issues',
      value: kpis?.issues_count ?? 0,
      icon: AlertTriangle,
      color: (kpis?.issues_count ?? 0) > 0 ? 'red' : 'amber',
    },
    {
      id: 'weekly-sales',
      label: 'Weekly Sales',
      value: weekly?.sales_detected ?? 0,
      icon: TrendingUp,
      color: 'blue',
    },
    {
      id: 'price-changes',
      label: 'Price Changes',
      value: weekly?.price_changes ?? 0,
      icon: BarChart3,
      color: 'purple',
    },
    {
      id: 'success-rate',
      label: 'Success Rate',
      value: `${successRate}%`,
      icon: Activity,
      color: successRate > 90 ? 'green' : 'red',
    },
    {
      id: 'ghost-units',
      label: 'Ghost Units',
      value: weekly?.ghost_units_pending ?? 0,
      detail: `${weekly?.ghost_units_cleaned ?? 0} cleaned this week`,
      icon: Eye,
      color: 'amber',
    },
  ]

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {cards.map((card) => (
          <KPICard
            key={card.id}
            card={card}
            onClick={onKpiClick ? () => onKpiClick(card.id) : undefined}
          />
        ))}
      </div>

      {/* Broker Health */}
      {data?.broker_health && data.broker_health.length > 0 && (() => {
        const sortedBrokers = [...data.broker_health].sort((a, b) => {
          if (b.errors !== a.errors) return b.errors - a.errors
          return b.total - a.total
        })
        const hasMore = sortedBrokers.length > BROKER_DISPLAY_LIMIT
        const visibleBrokers = showAllBrokers ? sortedBrokers : sortedBrokers.slice(0, BROKER_DISPLAY_LIMIT)

        return (
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-secondary-900">Broker Health</h3>
              <BarLegend />
            </div>
            <div className="divide-y divide-secondary-100">
              {visibleBrokers.map((broker) => (
                <BrokerHealthBar
                  key={broker.broker_id}
                  broker={broker}
                  isSelected={selectedBrokerId === broker.broker_id}
                  onSelect={() =>
                    setSelectedBrokerId(
                      selectedBrokerId === broker.broker_id ? null : broker.broker_id,
                    )
                  }
                />
              ))}
            </div>
            {hasMore && (
              <div className="flex justify-center mt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAllBrokers((prev) => {
                      if (prev) setSelectedBrokerId(null) // clear selection when collapsing
                      return !prev
                    })
                  }}
                  className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-800 font-medium"
                >
                  {showAllBrokers ? (
                    <>
                      Show top {BROKER_DISPLAY_LIMIT}
                      <ChevronUp className="h-4 w-4" />
                    </>
                  ) : (
                    <>
                      Show all {sortedBrokers.length} brokers
                      <ChevronDown className="h-4 w-4" />
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        )
      })()}
    </div>
  )
}
